"use client";

import type { ReactNode } from "react";
import { useMedia } from "react-use";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { cn } from "@/lib/utils";

interface ResponsiveModalProps {
  form: ReactNode;
  title: string;
  description?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  titleClassName?: string;
  descriptionClassName?: string;
  hideCloseIcon?: boolean;
  className?: string;
  headerAddon?: ReactNode;
  headerAddonClassName?: string;
}

export function ResponsiveModal({
  title,
  description,
  form,
  open,
  onOpenChange,
  titleClassName,
  descriptionClassName,
  hideCloseIcon,
  className,
  headerAddon,
  headerAddonClassName,
}: ResponsiveModalProps) {
  const isDesktop = useMedia("(min-width: 768px)");

  if (isDesktop) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          hideCloseIcon={hideCloseIcon}
          onInteractOutside={(e) => {
            e.preventDefault();
          }}
          className={cn("gap-6", className)}
        >
          <div className="flex flex-col gap-3">
            {headerAddon && (
              <div className={cn("text-left mb-3", headerAddonClassName)}>
                {headerAddon}
              </div>
            )}
            <DialogHeader className="flex flex-col text-center md:text-left">
              <DialogTitle
                className={cn(
                  "font-semibold text-lg leading-[18px]",
                  titleClassName,
                )}
              >
                {title}
              </DialogTitle>
              {description && (
                <DialogDescription className={descriptionClassName}>
                  {description}
                </DialogDescription>
              )}
            </DialogHeader>
          </div>

          {form}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent>
        <div className="px-4 pt-4">
          {headerAddon && (
            <div className={cn("mb-3 text-left", headerAddonClassName)}>
              {headerAddon}
            </div>
          )}
          <DrawerHeader className="px-0 text-left">
            <DrawerTitle className={titleClassName}>{title}</DrawerTitle>
            <DrawerDescription className={descriptionClassName}>
              {description}
            </DrawerDescription>
          </DrawerHeader>
        </div>
        <div className="overflow-y-auto px-8 pb-8">{form}</div>
      </DrawerContent>
    </Drawer>
  );
}
